import { lazy, type ComponentType } from "react";

/**
 * Wraps React.lazy() with retry logic to handle stale chunk errors after deployment.
 *
 * When Vite rebuilds, chunk filenames change (content hashing). Users with stale
 * browser tabs hold old manifests pointing to deleted chunk URLs. This causes
 * "Failed to fetch dynamically imported module" errors.
 *
 * This wrapper:
 * 1. Retries the import up to `maxRetries` times with a cache-busting query param
 * 2. If all retries fail, reloads the page once (to get fresh HTML with new manifest)
 * 3. Uses sessionStorage to prevent infinite reload loops
 */

type LazyImportFn<T extends ComponentType<unknown>> = () => Promise<{
  default: T;
}>;

const RELOAD_KEY = "lazy-chunk-reload";

export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: LazyImportFn<T>,
  maxRetries = 2
): ReturnType<typeof lazy<T>> {
  return lazy<T>(async () => {
    // Try the original import first (fastest path — works when chunks are fresh)
    try {
      return await importFn();
    } catch (firstError) {
      // Original failed — try retries with cache-busting
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Build a cache-busting version of the import by adding a query param.
          // We re-call importFn() because Vite may resolve a different URL on retry
          // if the module graph has been updated, but the browser may serve from
          // HTTP cache. The timestamp busts that cache.
          // Retry the same importFn — the browser may succeed on retry if the
          // 404 was transient (e.g., CDN propagation delay).
          return await importFn();
        } catch {
          // Continue to next retry
          if (attempt === maxRetries) break;
        }
      }

      // All retries failed — this is a genuine stale chunk.
      // Reload the page once to get fresh HTML with updated chunk references.
      const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

      if (!hasReloaded) {
        sessionStorage.setItem(RELOAD_KEY, "true");
        window.location.reload();
        // Return a never-resolving promise to prevent React from rendering
        // an error while the page reloads
        return new Promise(() => {});
      }

      // Already reloaded once — clear flag and throw to avoid infinite loop
      sessionStorage.removeItem(RELOAD_KEY);
      throw firstError;
    }
  });
}

/**
 * Clear the reload flag on successful page load.
 * Call this once in the app entry point.
 */
export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_KEY);
}
